import { useCallback, useEffect, useRef } from "react";
import {
  gaEventSearch,
  type SearchScopeKind,
  type SearchSource,
} from "../../../tools/analytics/domains/search.analytics";
import type { SearchCategory } from "../types";

export type TrackSearchQuery = {
  recordIntent: (term: string, source: SearchSource) => void;
  fireResultsSettled: (params: {
    term: string;
    category: SearchCategory;
    count: number;
    scope: SearchScopeKind;
  }) => void;
};

export function useTrackSearchQuery(): TrackSearchQuery {
  const sourceByTerm = useRef<Map<string, SearchSource>>(new Map());
  const firedKeys = useRef<Set<string>>(new Set());

  const recordIntent = useCallback((term: string, source: SearchSource) => {
    if (term.length === 0) return;
    sourceByTerm.current.set(term, source);
  }, []);

  const fireResultsSettled = useCallback(
    (params: {
      term: string;
      category: SearchCategory;
      count: number;
      scope: SearchScopeKind;
    }) => {
      if (params.term.length === 0) return;
      const key = `${params.term}|${params.category}|${params.scope}`;
      if (firedKeys.current.has(key)) return;
      firedKeys.current.add(key);
      const stored = sourceByTerm.current.get(params.term);
      const source: SearchSource = stored === undefined ? "typed" : stored;
      gaEventSearch({
        term: params.term,
        resultsCount: params.count,
        category: params.category,
        source,
        scope: params.scope,
      });
    },
    []
  );

  return { recordIntent, fireResultsSettled };
}

export function useTrackSearchSectionSettled(params: {
  isLoading: boolean;
  fire: TrackSearchQuery["fireResultsSettled"];
  term: string;
  category: SearchCategory;
  count: number;
  scope: SearchScopeKind;
}): void {
  const { isLoading, fire, term, category, count, scope } = params;
  useEffect(() => {
    if (isLoading) return;
    fire({ term, category, count, scope });
  }, [isLoading, fire, term, category, count, scope]);
}
